import { Pressable, RowButton } from "@/components/ui/pressable";
import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  useGetList,
  useNotify,
  useUpdate,
  type Identifier,
  type RaRecord,
} from "ra-core";
import { Check, ChevronDown, Pencil, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type IconType = ComponentType<{ className?: string }>;

/** One labelled field line: icon + label on the left, value on the right. */
export const FieldRow = ({
  icon: Icon,
  label,
  children,
}: {
  icon: IconType;
  label: string;
  children: ReactNode;
}) => (
  <div className="grid min-h-8 grid-cols-[8.5rem_minmax(0,1fr)] items-start gap-2 py-0.5">
    <div className="flex items-center gap-1.5 pt-1.5 text-sm text-muted-foreground">
      <Icon className="size-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </div>
    <div className="min-w-0">{children}</div>
  </div>
);

/** Collapsible group of fields (General, Business, Contact…). */
export const FieldGroup = ({
  title,
  children,
  defaultOpen = true,
  action,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  action?: ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="py-1.5">
      <div className="flex items-center justify-between gap-2">
        <Pressable
          className="flex flex-1 items-center justify-between py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          {title}
          <ChevronDown
            className={cn(
              "size-4 transition-transform",
              open ? "rotate-180" : "",
            )}
          />
        </Pressable>
        {action}
      </div>
      {open ? <div className="space-y-0.5 pt-0.5">{children}</div> : null}
    </section>
  );
};

/** Section with a header and an action (People, Projects…). */
export const RelatedSection = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <section className="border-t px-4 py-3">
    <div className="mb-2 flex items-center justify-between gap-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      {action ? (
        <div className="flex items-center gap-0.5">{action}</div>
      ) : null}
    </div>
    {children}
  </section>
);

const valueButtonClass =
  "flex min-h-8 w-full items-center rounded-md px-2 py-1 text-left text-sm hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50";

const Placeholder = ({ label }: { label: string }) => (
  <span className="text-muted-foreground">{label}</span>
);

/** Save one record's changed fields; previousData keeps the rest intact. */
export const useInlineSave = (resource: string, record: RaRecord) => {
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const save = (data: Record<string, unknown>) =>
    new Promise<boolean>((resolve) => {
      update(
        resource,
        { id: record.id, data, previousData: record },
        {
          mutationMode: "pessimistic",
          onSuccess: () => resolve(true),
          onError: (error) => {
            notify(error instanceof Error ? error.message : "Could not save", {
              type: "error",
            });
            resolve(false);
          },
        },
      );
    });
  return { save, isPending };
};

/** Click-to-edit text. Enter (or blur) saves, Escape cancels. */
export const InlineText = ({
  resource,
  record,
  source,
  placeholder,
  multiline = false,
  display,
  normalize = (value) => value.trim() || null,
}: {
  resource: string;
  record: RaRecord;
  source: string;
  placeholder: string;
  multiline?: boolean;
  display?: (value: string) => ReactNode;
  normalize?: (value: string) => unknown;
}) => {
  const current = String(record[source] ?? "");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(current);
  const { save, isPending } = useInlineSave(resource, record);
  useEffect(() => {
    if (!editing) setDraft(current);
  }, [current, editing]);

  const commit = async () => {
    const next = normalize(draft);
    if ((next ?? "") === (current || "")) {
      setEditing(false);
      return;
    }
    if (await save({ [source]: next })) setEditing(false);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setDraft(current);
      setEditing(false);
    } else if (event.key === "Enter" && (!multiline || event.metaKey)) {
      event.preventDefault();
      void commit();
    }
  };

  if (editing) {
    const shared = {
      autoFocus: true,
      value: draft,
      disabled: isPending,
      onBlur: () => void commit(),
      onKeyDown,
      "aria-label": placeholder,
    };
    return multiline ? (
      <Textarea
        {...shared}
        rows={3}
        onChange={(event) => setDraft(event.target.value)}
      />
    ) : (
      <Input
        {...shared}
        className="h-8"
        onChange={(event) => setDraft(event.target.value)}
      />
    );
  }

  return (
    <Pressable
      className={cn(valueButtonClass, multiline && "whitespace-pre-line")}
      onClick={() => setEditing(true)}
    >
      {current ? (
        (display?.(current) ?? <span className="truncate">{current}</span>)
      ) : (
        <Placeholder label={placeholder} />
      )}
    </Pressable>
  );
};

/** Pick one value from a short list. */
export const InlineSelect = ({
  resource,
  record,
  source,
  placeholder,
  choices,
}: {
  resource: string;
  record: RaRecord;
  source: string;
  placeholder: string;
  choices: ReadonlyArray<{ id: string | number; name: string }>;
}) => {
  const [open, setOpen] = useState(false);
  const { save, isPending } = useInlineSave(resource, record);
  const value = record[source];
  const selected = choices.find(
    (choice) => String(choice.id) === String(value),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Pressable className={valueButtonClass}>
          {selected ? (
            <span className="truncate">{selected.name}</span>
          ) : value ? (
            <span className="truncate">{String(value)}</span>
          ) : (
            <Placeholder label={placeholder} />
          )}
        </Pressable>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        <div className="max-h-64 overflow-y-auto">
          {choices.map((choice) => (
            <RowButton
              layout="none"
              density="none"
              className="flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-accent"
              key={String(choice.id)}
              disabled={isPending}
              onClick={async () => {
                if (await save({ [source]: choice.id })) setOpen(false);
              }}
            >
              {choice.name}
              {String(choice.id) === String(value) ? (
                <Check className="size-3.5" />
              ) : null}
            </RowButton>
          ))}
          {value != null && value !== "" ? (
            <RowButton
              layout="none"
              density="none"
              className="mt-1 flex w-full rounded border-t px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent"
              disabled={isPending}
              onClick={async () => {
                if (await save({ [source]: null })) setOpen(false);
              }}
            >
              Clear
            </RowButton>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};

type SearchOption = { id: Identifier; label: string; detail?: string };

/** Pick a related record with a search box (owner, account). */
export const InlineSearchPicker = ({
  resource,
  record,
  source,
  placeholder,
  searchResource,
  toOption,
  display,
  filter,
  extraSaveData,
  searchInBrowser = false,
}: {
  resource: string;
  record: RaRecord;
  source: string;
  placeholder: string;
  searchResource: string;
  /** Small lists without server search (team members): filter locally. */
  searchInBrowser?: boolean;
  toOption: (row: RaRecord) => SearchOption;
  display: ReactNode;
  filter?: Record<string, unknown>;
  extraSaveData?: Record<string, unknown>;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { save, isPending } = useInlineSave(resource, record);
  const { data = [] } = useGetList(
    searchResource,
    {
      filter: {
        ...filter,
        ...(!searchInBrowser && query.trim() ? { q: query.trim() } : {}),
      },
      pagination: { page: 1, perPage: searchInBrowser ? 200 : 20 },
      sort: { field: "id", order: "DESC" },
    },
    { enabled: open, staleTime: 30_000 },
  );
  const value = record[source];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Pressable className={valueButtonClass}>
          {value != null && value !== "" ? (
            display
          ) : (
            <Placeholder label={placeholder} />
          )}
        </Pressable>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-1" align="start">
        <Input
          autoFocus
          value={query}
          placeholder="Search…"
          className="mb-1 h-8"
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="max-h-64 overflow-y-auto">
          {data
            .map(toOption)
            .filter(
              (option) =>
                !searchInBrowser ||
                `${option.label} ${option.detail ?? ""}`
                  .toLowerCase()
                  .includes(query.trim().toLowerCase()),
            )
            .map((option) => {
              return (
                <RowButton
                  layout="none"
                  density="none"
                  className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                  key={String(option.id)}
                  disabled={isPending}
                  onClick={async () => {
                    if (await save({ [source]: option.id, ...extraSaveData }))
                      setOpen(false);
                  }}
                >
                  <span className="min-w-0">
                    <span className="block truncate">{option.label}</span>
                    {option.detail ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {option.detail}
                      </span>
                    ) : null}
                  </span>
                  {String(option.id) === String(value) ? (
                    <Check className="size-3.5 shrink-0" />
                  ) : null}
                </RowButton>
              );
            })}
          {data.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">
              No results
            </p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export type ChannelRow = Record<string, unknown> & {
  type?: string;
  isPrimary?: boolean;
  billing?: boolean;
};

/**
 * Edit a list of emails or phones in place. Rows keep their own properties
 * (type, billing flag), so editing an address keeps its role. The first row
 * is the primary one.
 */
export const InlineChannelList = ({
  rows: sourceRows,
  valueKey,
  placeholder,
  renderValue,
  typeChoices,
  onSave,
}: {
  rows: ChannelRow[];
  valueKey: "email" | "number";
  placeholder: string;
  renderValue?: (value: string) => ReactNode;
  /** Optional per-row type (e.g. Work / Billing for company channels). */
  typeChoices?: ReadonlyArray<{ id: string; name: string }>;
  onSave: (rows: ChannelRow[]) => Promise<boolean>;
}) => {
  const rows = sourceRows.filter((row) => String(row?.[valueKey] ?? "").trim());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ChannelRow[]>(rows);
  const [saving, setSaving] = useState(false);
  const lastInput = useRef<HTMLInputElement | null>(null);
  const defaultType = typeChoices?.[0]?.id ?? "Work";

  const start = () => {
    setDraft(rows.length ? rows : [{ [valueKey]: "", type: defaultType }]);
    setEditing(true);
  };
  const commit = async () => {
    const next = draft
      .map((row) => ({
        ...row,
        [valueKey]: String(row[valueKey] ?? "").trim(),
      }))
      .filter((row) => row[valueKey]);
    setSaving(true);
    const ok = await onSave(next);
    setSaving(false);
    if (ok) setEditing(false);
  };
  const updateRow = (index: number, patch: Partial<ChannelRow>) =>
    setDraft(
      draft.map((entry, position) =>
        position === index ? { ...entry, ...patch } : entry,
      ),
    );

  if (editing) {
    return (
      <div className="space-y-1.5 rounded-md border p-2">
        {draft.map((row, index) => (
          <div key={index} className="flex items-center gap-1">
            <Input
              ref={index === draft.length - 1 ? lastInput : undefined}
              autoFocus={index === 0}
              className="h-8 min-w-0 flex-1"
              value={String(row[valueKey] ?? "")}
              placeholder={placeholder}
              onChange={(event) =>
                updateRow(index, { [valueKey]: event.target.value })
              }
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void commit();
                } else if (event.key === "Escape") setEditing(false);
              }}
            />
            {typeChoices ? (
              <select
                aria-label="Type"
                className="h-8 rounded-md border bg-background px-1 text-xs"
                value={String(row.type ?? defaultType)}
                onChange={(event) =>
                  updateRow(index, { type: event.target.value })
                }
              >
                {typeChoices.map((choice) => (
                  <option key={choice.id} value={choice.id}>
                    {choice.name}
                  </option>
                ))}
              </select>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-8 p-0"
              aria-label="Remove"
              onClick={() =>
                setDraft(draft.filter((_, position) => position !== index))
              }
            >
              <X className="size-3.5" />
            </Button>
          </div>
        ))}
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2"
            onClick={() => {
              setDraft([...draft, { [valueKey]: "", type: defaultType }]);
              window.setTimeout(() => lastInput.current?.focus(), 0);
            }}
          >
            <Plus className="size-3.5" />
            Add
          </Button>
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-7 px-2"
              disabled={saving}
              onClick={() => void commit()}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Pressable className={valueButtonClass} onClick={start}>
      {rows.length ? (
        <span className="flex min-w-0 flex-wrap gap-1">
          {rows.map((row, index) => {
            const value = String(row[valueKey]);
            const billing = row.billing || row.type === "Billing";
            return (
              <span
                key={index}
                className="max-w-full truncate rounded-full border px-2 py-0.5 text-xs"
                title={billing ? "Used for billing" : undefined}
              >
                {renderValue?.(value) ?? value}
                {billing ? " · billing" : ""}
              </span>
            );
          })}
        </span>
      ) : (
        <Placeholder label={placeholder} />
      )}
    </Pressable>
  );
};

/** A contact's own email/phone list (`email_jsonb` / `phone_jsonb`). */
export const InlineChannels = ({
  resource,
  record,
  source,
  valueKey,
  placeholder,
  renderValue,
}: {
  resource: string;
  record: RaRecord;
  source: "email_jsonb" | "phone_jsonb";
  valueKey: "email" | "number";
  placeholder: string;
  renderValue?: (value: string) => ReactNode;
}) => {
  const { save } = useInlineSave(resource, record);
  return (
    <InlineChannelList
      rows={(record[source] as ChannelRow[] | null) ?? []}
      valueKey={valueKey}
      placeholder={placeholder}
      renderValue={renderValue}
      onSave={(rows) => save({ [source]: rows })}
    />
  );
};

/** Read-only value with an optional edit action (values managed elsewhere). */
export const ReadOnlyValue = ({
  children,
  onEdit,
  empty,
}: {
  children?: ReactNode;
  onEdit?: () => void;
  empty: string;
}) => (
  <div className="group flex min-h-8 items-center gap-1 px-2 py-1 text-sm">
    <span className="min-w-0 flex-1 truncate">
      {children ?? <Placeholder label={empty} />}
    </span>
    {onEdit ? (
      <Pressable
        className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
        aria-label="Edit"
        onClick={onEdit}
      >
        <Pencil className="size-3.5 text-muted-foreground" />
      </Pressable>
    ) : null}
  </div>
);
